/**
 * Flutter `isEmail` regex — verbatim from the `validators` pub package
 * (https://pub.dev/packages/validators) v3.0.0, `lib/validators.dart:5-6`.
 *
 * **Version pin (Samer review PR #312):** the regex string below is a
 * frozen copy of the regex at the EXACT version Flutter currently locks.
 * No npm `validators` dependency is added to this repo — the pattern is
 * inlined as a string so React parity is decoupled from any future
 * pub.dev / npm version drift. Flutter side is pinned by
 * `scrips_msp1_flutter_shared/scrips_shared_features/pubspec.lock`:
 *
 *     validators:
 *       version: "3.0.0"
 *       sha256:  884515951f831a9c669a41ed6c4d3c61c2a0e8ec6bca761a4480b28e99cecf5d
 *       source:  hosted (pub.dev)
 *
 * If Flutter ever bumps validators, copy the new regex literal here in
 * the same PR — DO NOT swap to an npm package that may drift independently
 * (this is an auth gate; behavior must match Flutter byte-for-byte).
 *
 * **Do NOT substitute HTML5 `type="email"`** — Flutter `login/body_widgets.dart:46`
 * calls `isEmail(value)` from this package, and CLAUDE.md's magic-strings rule
 * applies to validators that gate UX. HTML5's validator is more permissive
 * (accepts `a@b`); this one requires a TLD with at least one dot.
 *
 * Flutter applies `.toLowerCase()` before matching (`validators.dart:69`).
 * We mirror that — do NOT pre-normalize the displayed value; only lowercase
 * inside the validator call.
 *
 * Reference: `validators-3.0.0/lib/validators.dart:5-6` + `:68-70`.
 */

// Dart `r"..."` raw-string regex translated to a JS regex source string. All
// `\$` Dart escapes collapse to `$` (literal `$` inside a character class
// doesn't need escaping in JS), and `\/` collapses to `/` (JS regex source
// strings don't need to escape forward slash). Otherwise verbatim.
const EMAIL_PATTERN =
  "^((([a-z]|\\d|[!#$%&'*+\\-/=?^_`{|}~]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])+(\\.([a-z]|\\d|[!#$%&'*+\\-/=?^_`{|}~]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])+)*)|((\\x22)((((\\x20|\\x09)*(\\x0d\\x0a))?(\\x20|\\x09)+)?(([\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x7f]|\\x21|[\\x23-\\x5b]|[\\x5d-\\x7e]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])|(\\\\([\\x01-\\x09\\x0b\\x0c\\x0d-\\x7f]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF]))))*(((\\x20|\\x09)*(\\x0d\\x0a))?(\\x20|\\x09)+)?(\\x22)))@((([a-z]|\\d|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])|(([a-z]|\\d|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])([a-z]|\\d|-|\\.|_|~|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])*([a-z]|\\d|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])))\\.)+(([a-z]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])|(([a-z]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])([a-z]|\\d|-|\\.|_|~|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])*([a-z]|[\\u00A0-\\uD7FF\\uF900-\\uFDCF\\uFDF0-\\uFFEF])))$";

const EMAIL_REGEX = new RegExp(EMAIL_PATTERN);

/**
 * Returns true iff `value` matches Flutter's `isEmail` regex (validators
 * package 3.0.0). Empty string returns false. Mirrors Flutter `.toLowerCase()`
 * pre-match — accepts `User@Example.com`.
 */
export function isFlutterEmail(value: string): boolean {
  if (!value) return false;
  return EMAIL_REGEX.test(value.toLowerCase());
}

/** "" / null / undefined / whitespace-only check — mirrors Flutter `isBlank`. */
export function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim().length === 0;
}
